import React, { useState } from 'react';
import { bindIntent, proposeIntent } from '../../services/protectBuild';

/**
 * The Semantic Decision Layer — the intent this agent is bound to.
 *
 * <h3>Why only approved intents can be attached</h3>
 *
 * A binding is refused unless the template version is APPROVED, and the
 * Execution-Ready gate checks that a binding exists. Offering a draft would be
 * offering a bind the platform rejects, inside a sequence whose promise is that
 * it reports what happened rather than what was hoped.
 *
 * <h3>Why describing one does not finish the job</h3>
 *
 * A new version needs an authored policy and an approver who is not its author.
 * That is enforced in the service and again by a database CHECK, so the most
 * this can do is draft it, submit it for review, and show it as approval
 * pending. Saying anything stronger would be a promise the platform exists to
 * refuse.
 *
 * <h3>Why it can be left for later</h3>
 *
 * Binding is independently set — the platform allows it in any phase, including
 * Act, and the readiness gate checks the binding without ever blocking the act
 * of setting one. So an agent can come under protection now and be given its
 * decision layer when the right one exists, as long as the surface is honest
 * that the gate is still open.
 */
export default function IntentAsk({
  agentId, intents = [], error, scope, onAnswer, busy = false,
}) {
  const [pickedId, setPickedId] = useState(intents[0]?.versionId || null);
  const [authoring, setAuthoring] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);
  const [pending, setPending] = useState(null);

  const picked = intents.find((i) => i.versionId === pickedId) || null;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const attach = async () => {
    if (!picked) return;
    setWorking(true); setProblem(null);
    try {
      await bindIntent(agentId, picked.versionId);
      onAnswer({ bound: true, label: picked.label, versionId: picked.versionId });
    } catch (e) {
      setProblem(e?.message || 'The binding was refused.');
    } finally {
      setWorking(false);
    }
  };

  const propose = async () => {
    setWorking(true); setProblem(null);
    try {
      const out = await proposeIntent({
        name: form.name.trim(),
        description: form.description.trim(),
        orgId: scope?.orgId,
        tenantId: scope?.tenantId,
      });
      setPending(out);
      onAnswer({ bound: false, submitted: out.submitted, label: form.name.trim(),
        templateId: out.templateId });
    } catch (e) {
      setProblem(e?.message || 'The intent could not be drafted.');
    } finally {
      setWorking(false);
    }
  };

  if (pending) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <span className="p7-status p7-status--hold" style={{ alignSelf: 'flex-start' }}>
          Approval pending
        </span>
        <p style={NOTE}>
          <strong>{form.name.trim()}</strong> is drafted and
          {pending.submitted ? ' submitted for review' : ' waiting to be submitted'}. You cannot
          approve your own intent — somebody with policy or compliance admin has to approve it,
          and it needs its policy authored first. Until then this agent has no decision layer
          attached, and its readiness gate will say so.
        </p>
        {pending.problem ? (
          <p className="p7-note p7-note--hold" style={{ margin: 0 }}>{pending.problem}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      {error ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          Could not read your intents just now. You can still attach one later.
        </p>
      ) : null}

      {!authoring && intents.length === 0 && !error ? (
        <p style={NOTE}>
          No approved intent exists in this tenant yet. Describe one and it goes for approval,
          or attach one later once there is one to attach.
        </p>
      ) : null}

      {!authoring && intents.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {intents.map((i) => {
            const on = i.versionId === pickedId;
            return (
              <button key={i.versionId} type="button" aria-pressed={on}
                      onClick={() => setPickedId(i.versionId)}
                      style={{
                        textAlign: 'left', cursor: 'pointer', padding: '9px 13px',
                        borderRadius: 'var(--p7-radius-control)',
                        border: `1px solid ${on ? 'var(--p7-accent)' : 'var(--p7-line-strong)'}`,
                        background: on ? 'var(--p7-accent-bg)' : 'var(--p7-surface)',
                        font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink)', maxWidth: '280px',
                      }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{i.label}</span>
                {i.description ? (
                  <span style={{ display: 'block', marginTop: '1px',
                                 font: 'var(--p7-text-caption)',
                                 fontFamily: 'var(--p7-font-sans)',
                                 color: 'var(--p7-ink-subtle)' }}>
                    {i.description}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {authoring ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          <Field label="Name" value={form.name} onChange={set('name')}
                 placeholder="renewals-assistant" />
          <Field label="What it is for" value={form.description} onChange={set('description')}
                 placeholder="Reads CRM renewals and drafts summaries." />
          <p style={NOTE}>
            This goes for approval. You cannot approve your own intent, and its policy has to be
            authored before anyone can.
          </p>
        </div>
      ) : null}

      {problem ? <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p> : null}

      <div style={{ display: 'flex', gap: '9px', alignItems: 'center', flexWrap: 'wrap' }}>
        {authoring ? (
          <>
            <button type="button" className="p7-btn p7-btn--primary"
                    disabled={busy || working || !form.name.trim() || !form.description.trim()}
                    onClick={propose}>
              {working ? 'Submitting…' : 'Submit for approval'}
            </button>
            <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                    onClick={() => { setAuthoring(false); setProblem(null); }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {intents.length > 0 ? (
              <button type="button" className="p7-btn p7-btn--primary"
                      disabled={busy || working || !picked} onClick={attach}>
                {working ? 'Binding…' : 'Bind it'}
              </button>
            ) : null}
            <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                    onClick={() => setAuthoring(true)}>
              Describe your own
            </button>
          </>
        )}
        {/* Always available. The gate checks the binding; it never blocks
            setting one, so this is a decision to make later, not a dead end. */}
        <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                onClick={() => onAnswer({ bound: false, deferred: true })}>
          Attach one later
        </button>
      </div>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: '3px',
                     font: '600 10.5px/16px var(--p7-font-sans)', letterSpacing: '.08em',
                     textTransform: 'uppercase', color: 'var(--p7-ink-subtle)' }}>
        {label}
      </span>
      <input
        {...rest}
        style={{ width: '100%', padding: '9px 12px',
                 border: '1px solid var(--p7-line-strong)',
                 borderRadius: 'var(--p7-radius-control)',
                 font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}
      />
    </label>
  );
}

const NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};
